<template>
    <section v-if="goalIds.length > 0" class="rounded-xl border border-border bg-surface-raised px-4 py-3 flex flex-col gap-3">
        <div class="flex items-start justify-between gap-3">
            <div class="flex min-w-0 items-center gap-3">
                <div class="size-12 shrink-0 rounded-xl bg-chip-neutral-bg text-ink-muted flex items-center justify-center">
                    <i class="bi bi-book text-xl" aria-hidden="true"></i>
                </div>
                <div class="flex flex-col gap-1 min-w-0">
                    <h2 class="scaffold-body text-ink">{{ t("journeys.suggestedResourcesSection.title") }}</h2>
                    <p class="body-small text-ink-muted">{{ t("journeys.suggestedResourcesSection.subtitle") }}</p>
                </div>
            </div>
            <BaseButton
                type="button"
                theme="tertiary"
                size="sm"
                icon-only
                class="shrink-0"
                :disabled="isFetching"
                :aria-busy="isFetching"
                :aria-label="t('journeys.suggestedResourcesSection.refresh')"
                @click="refreshResources">
                <i class="bi bi-arrow-clockwise" :class="{ 'animate-spin': isFetching }" aria-hidden="true"></i>
            </BaseButton>
        </div>

        <div v-if="isFetching" class="flex flex-col gap-2">
            <div v-for="n in 3" :key="n" class="rounded-lg border border-border px-4 py-3 flex items-center gap-3">
                <SkeletonLoader class="size-9 shrink-0 rounded-lg" />
                <div class="min-w-0 flex-1 flex flex-col gap-2">
                    <SkeletonLoader class="h-4 w-2/3 rounded-md" />
                    <SkeletonLoader class="h-3 w-1/2 rounded-md" />
                </div>
            </div>
        </div>

        <p v-else-if="error" role="alert" class="body-small text-ink-muted">{{ t("journeys.suggestedResourcesSection.unavailable") }}</p>

        <p v-else-if="resources.length === 0" class="body-small text-ink-muted">{{ t("journeys.suggestedResourcesSection.noMatches") }}</p>

        <div v-else class="flex flex-col gap-2">
            <a
                v-for="resource in sortedResources"
                :key="resourceKey(resource)"
                :href="resource.url ?? '#'"
                :target="resource.url ? '_blank' : undefined"
                :rel="resource.url ? 'noopener noreferrer' : undefined"
                class="rounded-lg border border-border px-4 py-3 flex min-w-0 items-start gap-3 transition-colors"
                :class="resource.url ? 'hover:border-border-strong' : 'pointer-events-none'"
                :aria-disabled="!resource.url"
                @mouseenter="emit('hover-goal', linkedGoalId(resource))"
                @mouseleave="emit('hover-goal', null)"
                @focus="emit('hover-goal', linkedGoalId(resource))"
                @blur="emit('hover-goal', null)">
                <div :class="contentTypeIconTileClass(resource.content_type)">
                    <i :class="contentTypeIcon(resource.content_type)" aria-hidden="true"></i>
                </div>
                <div class="min-w-0 flex-1 flex flex-col gap-1">
                    <div class="flex items-start justify-between gap-3">
                        <h3 class="body-small-semibold text-ink min-w-0">{{ resource.title }}</h3>
                        <i v-if="resource.url" class="bi bi-box-arrow-up-right shrink-0 text-ink-muted text-sm mt-1" aria-hidden="true"></i>
                    </div>
                    <p v-if="resource.summary" class="details text-ink-muted line-clamp-2">{{ resource.summary }}</p>
                    <div class="details text-ink-muted flex flex-wrap items-center gap-2 pt-1">
                        <div class="flex min-w-0 flex-wrap items-center gap-2">
                            <span v-if="resource.content_type" :class="contentTypeChipClass(resource.content_type)">
                                {{ contentTypeLabel(resource.content_type) }}
                            </span>
                            <span
                                v-if="resource.duration_minutes"
                                class="inline-flex items-center gap-1 rounded-full border border-border bg-surface-raised px-2.5 py-1 text-ink-muted">
                                <i class="bi bi-clock" aria-hidden="true"></i>
                                {{ t("journeys.suggestedResourcesSection.minutes", { count: resource.duration_minutes }) }}
                            </span>
                        </div>
                        <span
                            v-if="goalTitleForResource(resource)"
                            class="ml-auto max-w-[50%] truncate rounded-full bg-chip-neutral-bg px-2.5 py-1 text-[11px] font-medium text-ink-muted">
                            {{ goalTitleForResource(resource) }}
                        </span>
                    </div>
                </div>
            </a>
        </div>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { type Goal } from "~vue/types/goals";
import { computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";

interface SuggestedResource {
    external_id: string;
    title: string;
    url: string | null;
    summary: string | null;
    content_type: string | null;
    duration_minutes: number | null;
    skills: string[];
    goal_ids: string[];
    search_query: string;
    search_skill: string | null;
}

interface SuggestedResourcesResponse {
    results: SuggestedResource[];
}

const props = defineProps<{
    goals: Goal[];
}>();

const emit = defineEmits<{
    "hover-goal": [goalId: string | null];
}>();

const { t } = useI18n();

const goalIds = computed(() => props.goals.map((goal) => goal.id).sort());
const goalKey = computed(() => goalIds.value.join("|"));

const { data, error, isFetching, execute } = useFetch("/api/goals/suggested-resources/", { immediate: false }).json<SuggestedResourcesResponse>();

const resources = computed(() => data.value?.results ?? []);

const goalsById = computed(() => new Map(props.goals.map((goal) => [goal.id, goal.title])));

const sortedResources = computed(() =>
    resources.value.slice().sort((a, b) => {
        const goalOrderDelta = goalOrder(a) - goalOrder(b);
        if (goalOrderDelta !== 0) {
            return goalOrderDelta;
        }
        return (a.duration_minutes ?? Number.POSITIVE_INFINITY) - (b.duration_minutes ?? Number.POSITIVE_INFINITY);
    }),
);

function goalTitleForResource(resource: SuggestedResource): string | null {
    const goalId = linkedGoalId(resource);
    return goalId ? (goalsById.value.get(goalId) ?? null) : null;
}

function linkedGoalId(resource: SuggestedResource): string | null {
    return resource.goal_ids[0] ?? null;
}

function resourceKey(resource: SuggestedResource): string {
    return `${linkedGoalId(resource) ?? "goal"}-${resource.external_id}`;
}

function goalOrder(resource: SuggestedResource): number {
    const goalId = linkedGoalId(resource);
    const index = goalId ? props.goals.findIndex((goal) => goal.id === goalId) : -1;
    return index === -1 ? Number.POSITIVE_INFINITY : index;
}

const contentTypeIcons: Record<string, string> = {
    article: "bi bi-file-text",
    assessment: "bi bi-ui-checks",
    book: "bi bi-book",
    course: "bi bi-mortarboard",
    event: "bi bi-calendar-event",
    podcast: "bi bi-mic",
    video: "bi bi-play-btn",
};

function contentTypeLabel(contentType: string): string {
    const normalized = contentType.trim().toLowerCase();
    switch (normalized) {
        case "article":
            return t("journeys.suggestedResourcesSection.contentTypes.article");
        case "assessment":
            return t("journeys.suggestedResourcesSection.contentTypes.assessment");
        case "book":
            return t("journeys.suggestedResourcesSection.contentTypes.book");
        case "course":
            return t("journeys.suggestedResourcesSection.contentTypes.course");
        case "event":
            return t("journeys.suggestedResourcesSection.contentTypes.event");
        case "podcast":
            return t("journeys.suggestedResourcesSection.contentTypes.podcast");
        case "video":
            return t("journeys.suggestedResourcesSection.contentTypes.video");
        default:
            return contentType;
    }
}

interface ContentTypeStyle {
    chip: string;
    iconTile: string;
}

const fallbackContentTypeStyle: ContentTypeStyle = {
    chip: "border border-chip-neutral-border bg-chip-neutral-bg text-ink-muted",
    iconTile: "bg-chip-neutral-bg text-ink-muted",
};

const contentTypeStyle: ContentTypeStyle = {
    chip: "border border-chip-neutral-border bg-chip-neutral-bg text-ink-muted",
    iconTile: "bg-chip-neutral-bg text-ink-muted",
};

function contentTypeIcon(contentType: string | null): string {
    const normalized = contentType?.trim().toLowerCase();
    if (!normalized) {
        return "bi bi-collection-play";
    }
    return contentTypeIcons[normalized] ?? "bi bi-collection-play";
}

function contentTypeChipClass(contentType: string): string {
    const normalized = contentType.trim().toLowerCase();
    const colorClass = normalized ? contentTypeStyle.chip : fallbackContentTypeStyle.chip;
    return `inline-flex items-center rounded-full px-2.5 py-1 capitalize ${colorClass}`;
}

function contentTypeIconTileClass(contentType: string | null): string {
    const normalized = contentType?.trim().toLowerCase();
    const colorClass = normalized ? contentTypeStyle.iconTile : fallbackContentTypeStyle.iconTile;
    return `size-9 shrink-0 rounded-lg flex items-center justify-center ${colorClass}`;
}

async function refreshResources() {
    if (!goalKey.value) {
        data.value = { results: [] };
        return;
    }
    await execute();
}

onMounted(refreshResources);
watch(goalKey, refreshResources);
</script>
